<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import ProfileService from "../../services/profileService.js";
import { useTheme } from "../../composables/useTheme.js";
import Spinner from "./Spinner.vue";
import DeleteAccountModal from "./DeleteAccountModal.vue";

const emit = defineEmits(["close"]);
const router = useRouter();
const profileService = new ProfileService();
const { theme, setTheme } = useTheme();

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

// ─── Personal details (old GraphQL profile API) ────────────────────────────
const isLoadingProfile = ref(true);
const name = ref("");
const email = ref("");
const isB2BUser = ref(false);
const canSelfDelete = computed(() => !isB2BUser.value);

const initials = computed(() => {
  const parts = name.value.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
});

onMounted(async () => {
  isLoadingProfile.value = true;
  try {
    const { name: n = "", email: e = "", isB2BUser: b2b = false } = await profileService.getMyProfile();
    name.value = n ?? "";
    email.value = e ?? "";
    isB2BUser.value = b2b;
    editName.value = name.value;
  } finally {
    isLoadingProfile.value = false;
  }
});

// ─── Edit profile ───────────────────────────────────────────────────────────
const isEditingProfile = ref(false);
const editName = ref("");
const isSavingProfile = ref(false);

function startEditProfile() {
  isEditingProfile.value = true;
}
function cancelEditProfile() {
  editName.value = name.value;
  isEditingProfile.value = false;
}
async function saveProfile() {
  if (!editName.value.trim() || editName.value === name.value) {
    isEditingProfile.value = false;
    return;
  }
  isSavingProfile.value = true;
  try {
    await profileService.updateUserProfile({ email: email.value, name: editName.value.trim() });
    name.value = editName.value.trim();
    isEditingProfile.value = false;
  } finally {
    isSavingProfile.value = false;
  }
}

// ─── Delete account ─────────────────────────────────────────────────────────
const showDeleteAccountModal = ref(false);

// ─── Clear cache (old IndexedDB cache) ──────────────────────────────────────
const cacheSizeLabel = ref("—");
const isClearingCache = ref(false);

onMounted(async () => {
  if (!navigator.storage?.estimate) return;
  const { usage } = await navigator.storage.estimate();
  cacheSizeLabel.value = `${Math.round((usage ?? 0) / (1024 * 1024))}MB`;
});

function clearCache() {
  isClearingCache.value = true;
  indexedDB.deleteDatabase("FOLIA_CACHE");
  router.push({ name: "v2-home" }).then(() => location.reload());
}
</script>

<template>
  <div data-testid="settings-modal-backdrop" class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[400] flex items-center justify-center p-4" @click.self="close">
    <div data-testid="settings-modal" class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[620px] flex flex-col overflow-hidden z-[420]">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 shrink-0">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">Settings</h2>
        <button
          data-testid="settings-modal-close"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

      <div data-testid="settings-modal-loading" v-if="isLoadingProfile" class="flex justify-center py-16"><Spinner size="26px" /></div>

      <div v-else class="flex flex-col">

        <!-- Personal details -->
        <div class="flex items-start gap-6 px-6 py-5">
          <div class="flex-1 flex flex-col gap-3 min-w-0">
            <div>
              <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0">Personal Details</h3>
              <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0">Manage details of your personal profile.</p>
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-200 w-16 shrink-0">Name :</label>
              <input
                data-testid="settings-name-input"
                v-model="editName"
                type="text"
                :readonly="!isEditingProfile"
                :class="isEditingProfile
                  ? 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900'
                  : 'bg-slate-100 dark:bg-slate-800 border-transparent'"
                class="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-200 outline-none transition-shadow"
                @keydown.enter="saveProfile"
              />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-semibold text-slate-700 dark:text-slate-200 w-16 shrink-0">Email :</label>
              <input
                data-testid="settings-email-input"
                :value="email"
                type="email"
                disabled
                class="flex-1 min-w-0 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-lg px-3 py-2 text-sm text-slate-500 dark:text-slate-400 outline-none"
              />
            </div>

            <!-- Edit actions -->
            <div v-if="isEditingProfile" class="flex items-center justify-end gap-2 pt-1">
              <button
                data-testid="settings-cancel-button"
                class="px-4 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                @click="cancelEditProfile"
              >
                Cancel
              </button>
              <button
                data-testid="settings-save-button"
                :disabled="isSavingProfile"
                class="px-4 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50"
                @click="saveProfile"
              >
                {{ isSavingProfile ? "Saving…" : "Save" }}
              </button>
            </div>
          </div>

          <div class="w-px self-stretch bg-slate-200 dark:bg-slate-700 shrink-0" />

          <div class="flex flex-col items-center gap-3 shrink-0 pt-1">
            <div data-testid="settings-avatar" class="flex items-center justify-center w-20 h-20 rounded-full bg-[#7ba7d4] text-white text-2xl font-semibold select-none">
              {{ initials }}
            </div>
            <button
              data-testid="settings-edit-profile-button"
              v-if="!isEditingProfile"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
              @click="startEditProfile"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Edit profile
            </button>
          </div>
        </div>

        <div class="h-px bg-slate-200 dark:bg-slate-700" />

        <!-- Appearance -->
        <div class="flex items-center justify-between px-6 py-5 gap-4">
          <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0 shrink-0">Appearance</h3>
          <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
            <button
              :data-testid="`settings-theme-${opt.key}`"
              v-for="opt in [
                { key: 'system', label: 'System' },
                { key: 'light',  label: 'Light' },
                { key: 'dark',   label: 'Dark' },
              ]"
              :key="opt.key"
              :class="theme === opt.key
                ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100'
                : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
              class="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all border-0 cursor-pointer"
              @click="setTheme(opt.key)"
            >
              <svg v-if="opt.key === 'system'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="20" height="13" rx="2" stroke="currentColor" stroke-width="1.8" />
                <path d="M8 21h8M12 17v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <svg v-else-if="opt.key === 'light'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8" />
                <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="h-px bg-slate-200 dark:bg-slate-700" />

        <!-- Delete account -->
        <div v-if="canSelfDelete" class="px-6 py-5">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0">Delete Account</h3>
              <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0">Delete your account and the data associated with it.</p>
            </div>
            <button
              data-testid="settings-delete-account-button"
              class="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-500 dark:text-red-400 text-sm font-semibold bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer shrink-0"
              @click="showDeleteAccountModal = true"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Delete
            </button>
          </div>
        </div>

        <div v-if="canSelfDelete" class="h-px bg-slate-200 dark:bg-slate-700" />

        <!-- Clear cache -->
        <div class="flex items-center justify-between gap-4 px-6 py-5">
          <div>
            <h3 class="text-[15px] font-bold text-slate-800 dark:text-slate-100 m-0">
              Clear Cache <span class="font-normal text-slate-400 dark:text-slate-500">(Cache size : {{ cacheSizeLabel }})</span>
            </h3>
            <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0">Remove all cached data to free up storage.</p>
          </div>
          <button
            data-testid="settings-clear-cache-button"
            :disabled="isClearingCache"
            class="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer disabled:opacity-50 shrink-0"
            @click="clearCache"
          >
            {{ isClearingCache ? "Clearing…" : "Clear Cache" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete account confirmation, layered above this modal -->
    <DeleteAccountModal
      v-if="showDeleteAccountModal"
      :user-email="email"
      @close="showDeleteAccountModal = false"
    />
  </div>
</template>
